import { getMarkerIconSize, getMarkerRadius, getMarkerRing, getSelectedColor } from "components/map/markerVisualTokens";
import type { CSSProperties, KeyboardEvent } from "react";
import styles from "./BaseMap.module.scss";
import icons from "./markerIcons.json";
import { POI_DOT, POI_FILL, type PoiMarkerShape } from "./mapModel";

/**
 * The marker a nearby place wears on every partner map: the app's type icon in the tint of its group, following
 * design/map-markers: white ring and soft shadow, light tint with a black icon, a name plate below, a photo card
 * above on hover. A partner's own markers (its hostel, its logo) are built from the same pieces.
 */

const ICON_MARKUP = icons as Record<string, string>;

/** the room a pill takes above a marker */
const PILL_ROOM = 24;

export const cx = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(" ");

/** One of the marker drawings, 32 x 32. The markup is our own static data, never user input. */
export const MarkerIcon = ({ name, size }: { name: string; size: number }) => (
	<svg viewBox="0 0 32 32" style={{ width: size, height: size }} aria-hidden="true" dangerouslySetInnerHTML={{ __html: ICON_MARKUP[name] ?? "" }} />
);

export const onActivate = (action: () => void) => (e: KeyboardEvent) => {
	if (e.key === "Enter" || e.key === " ") {
		e.preventDefault();
		action();
	}
};

interface PoiMarkerProps<T extends PoiMarkerShape> {
	data: T;
	dot: boolean;
	plate: boolean;
	scale: number;
	/** the page drives the hover card (a mouse is over the marker or its list row) instead of CSS :hover, which a touch screen would keep after a tap */
	hovered?: boolean;
	/** a small label above the marker, like a start time */
	pill?: string;
	/** the name plate hangs above the marker (over its pill) instead of below it, where something else is */
	plateAbove?: boolean;
	/** the name plate's font size in px, when it should not follow `scale` (a map whose markers grow past it keeps one size of name) */
	plateSize?: number;
	/** a partner's own colour (a `#rrggbb`) in place of the group's tint: the marker, its dot and its selected rim wear it */
	fill?: string;
	/** the icon's colour on a `fill`; the tints take the dark ink of the stylesheet */
	iconColor?: string;
	/** the rim of the marker when it is open, when that is not the `fill` (a black marker with a bright rim) */
	ringColor?: string;
	onSelect?: (data: T) => void;
}

/** The pill of a place that has a logo, 78 x 42 at the map's full scale (the designer's sister restaurant marker), or its round form, 46px across with the logo small inside. */
const LOGO_PILL = { w: 78, h: 42, logoW: 58 };
const LOGO_ROUND = { w: 46, h: 46, logoW: 24 };

/**
 * A place with a logo: its wordmark on a white pill with a rim and a name plate in its own colour (and ink), shown at every zoom (it is the partner's
 * other place, not one of many dots); `round`, a circle filled with that colour and rimmed in its `ring`. Open, it wears the ring of a selected marker;
 * a mouse over it brings out its photo above.
 */
const LogoMarker = <T extends PoiMarkerShape>({
	data,
	scale,
	hovered,
	fill,
	ringColor,
	onSelect,
}: Pick<PoiMarkerProps<T>, "data" | "scale" | "hovered" | "onSelect" | "ringColor"> & { fill: string }) => {
	const k = Math.max(scale, 0.8);
	const ring = getMarkerRing("poi", data.active, data.ring ?? ringColor ?? fill);
	const shape = data.round ? LOGO_ROUND : LOGO_PILL;
	const h = shape.h * k;
	return (
		<div className={cx(styles.mk, hovered !== undefined && styles.controlled, hovered && styles.on)}>
			<span
				className={cx(styles.md, onSelect && styles.clickable)}
				style={{
					width: shape.w * k,
					height: h,
					borderRadius: 999,
					background: data.round ? fill : "#fff",
					border: data.active ? ring.border : `2.5px solid ${data.round ? (data.ring ?? fill) : fill}`,
					boxShadow: data.active ? ring.boxShadow : "0 0 0 2px #fff, 0 6px 14px rgba(27, 22, 17, 0.4)",
				}}
				{...(onSelect
					? { role: "button", tabIndex: 0, "aria-label": data.name, onClick: () => onSelect(data), onKeyDown: onActivate(() => onSelect(data)) }
					: { "aria-hidden": true })}
			>
				<img src={data.logo} alt="" style={{ width: shape.logoW * k, height: "auto" }} />
			</span>
			{data.photo && !data.active && (
				<span className={cx(styles.hov, styles.hovUp)} style={{ "--r": `${h / 2}px` } as CSSProperties}>
					<span className={styles.hovCard}>
						<img src={data.photo} alt="" />
					</span>
				</span>
			)}
			<span
				className={styles.cap}
				style={{
					top: h / 2 + 3,
					background: fill,
					color: data.ink ?? "#fff",
					font: `400 ${12.5 * k}px/1.2 var(--f-head)`,
					padding: "3px 9px",
					borderRadius: 8,
				}}
			>
				{data.plate ?? data.name}
			</span>
		</div>
	);
};

export const PoiMarker = <T extends PoiMarkerShape>({
	data,
	dot,
	plate,
	scale,
	hovered,
	pill,
	plateAbove = false,
	plateSize,
	fill: ownFill,
	iconColor,
	ringColor,
	onSelect,
}: PoiMarkerProps<T>) => {
	const fill = ownFill ?? POI_FILL[data.grp];
	if (data.logo) return <LogoMarker data={data} scale={scale} hovered={hovered} fill={fill} ringColor={ringColor} onSelect={onSelect} />;
	const r = getMarkerRadius(dot ? "dot" : "poi", 0) * (dot ? 1 : Math.max(scale, 0.8));
	// the rim of a selected marker is its own colour, as the tints' saturated one is; getSelectedColor knows only the tints
	const ring = getMarkerRing(dot ? "dot" : "poi", data.active, ringColor ?? ownFill ?? getSelectedColor(fill));
	return (
		<div className={cx(styles.mk, hovered !== undefined && styles.controlled, hovered && styles.on)}>
			<span
				className={cx(styles.md, onSelect && styles.clickable)}
				style={{
					width: 2 * r,
					height: 2 * r,
					background: dot ? (ownFill ?? POI_DOT[data.grp]) : fill,
					color: iconColor,
					border: ring.border,
					boxShadow: ring.boxShadow,
				}}
				{...(onSelect
					? { role: "button", tabIndex: 0, "aria-label": data.name, onClick: () => onSelect(data), onKeyDown: onActivate(() => onSelect(data)) }
					: { "aria-hidden": true })}
			>
				{!dot && <MarkerIcon name={data.icon} size={getMarkerIconSize(r)} />}
			</span>
			{pill && (
				<span className={styles.pill} style={{ "--r": `${r}px` } as CSSProperties}>
					{pill}
				</span>
			)}
			{!dot && data.photo && !data.active && (
				<span className={cx(styles.hov, styles.hovUp)} style={{ "--r": `${r}px` } as CSSProperties}>
					<span className={styles.hovCard}>
						<img src={data.photo} alt="" />
					</span>
					{!plate && <span className={styles.hovCap}>{data.name}</span>}
				</span>
			)}
			{!dot && (plate || data.active) && (
				<span
					className={styles.cap}
					style={{ ...(plateAbove ? { bottom: r + 1 + (pill ? PILL_ROOM : 0) } : { top: r + 1 }), fontSize: plateSize ?? Math.max(11, 12.5 * scale) }}
				>
					{data.name}
				</span>
			)}
		</div>
	);
};
